import { useMutation } from '@apollo/client';
import classNames from 'classnames';
import React, { useRef } from 'react';
import { flushSync } from 'react-dom';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import type { BannerEditorRef } from '../components/BannerEditor';
import BannerEditor from '../components/BannerEditor';
import Button from '../components/Button';
import InPlaceEditControls from '../components/InPlaceEditControls';
import InPlaceEditable from '../components/InPlaceEditable';
import { MenuItem, MenuSeparator } from '../components/Menu';
import OverflowMenu from '../components/OverflowMenu';
import Tooltip from '../components/Tooltip';
import TopicOrUserCollapsibleContent from '../components/TopicOrUserCollapsibleContent';
import {
  BEACON_TOPIC_EDIT,
  BEACON_TOPIC_OVERFLOW_MENU,
} from '../constants/onboarding';
import { IS_CAPACITOR } from '../constants/platform';
import RichTextDescriptionEditor from '../editor/RichTextDescriptionEditor';
import {
  TopicPostsType,
  TopicPrivacy,
  UserType,
} from '../graphql/generated/graphql';
import UPDATE_TOPIC_MUTATION from '../graphql/updateTopicMutation';
import addIcon from '../icons/add.svg';
import clockIcon from '../icons/clock.svg';
import copyIcon from '../icons/copy.svg';
import editIcon from '../icons/edit.svg';
import favoriteIcon from '../icons/favorite.svg';
import memberIcon from '../icons/member.svg';
import memberRemoveIcon from '../icons/memberRemove.svg';
import reorderIcon from '../icons/reorder.svg';
import settingsIcon from '../icons/settings.svg';
import subtopicsIcon from '../icons/subtopics.svg';
import topicIcon from '../icons/topic.svg';
import trashIcon from '../icons/trash.svg';
import TutorialBeacon from '../onboarding/tutorial/TutorialBeacon';
import ReorderTopicsModal from '../sidebar/ReorderTopicsModal';
import UIActions from '../ui/UIActions';
import AdminBadge from '../user/AdminBadge';
import { useAllUsersMap } from '../user/useAllUsers';
import populateObjects from '../utils/populateObjects';
import Sizes from '../utils/responsive/Sizes';
import isSize from '../utils/responsive/isSize';
import fullOrgUrl from '../utils/url/fullOrgUrl';
import topicPath, { publicTopicPath } from '../utils/url/topicPath';
import { usePublicContentPermissions } from '../utils/useAllowPublicContentBy';
import useBeforeRouterLeaveConfirmWithModal from '../utils/useBeforeRouterLeaveConfirmWithModal';
import TopicActionButtons from './TopicActionButtons';
import TopicActivitiesPanel from './TopicActivitiesPanel';
import TopicBreadcrumbs from './TopicBreadcrumbs';
import TopicNameWithIcon from './TopicNameWithIcon';
import type { EditTopicPayload, EditTopicState } from './TopicView';
import styles from './TopicViewHeader.module.scss';
import EditTopicModal from './modal/EditTopicModal';
import NewTopicModal from './modal/NewTopicModal';
import topicPolicyCheck, {
  isRequestor,
  isUserDirectlyInTopic,
  isUserInTopic,
  MEMBER_EDITABLE_ALL,
  TopicPolicyAction,
} from './topicPolicyCheck';
import { useAllTopicMap } from './useAllTopics';
import useDeleteTopic from './useDeleteTopic';
import useToggleFavoriteTopic from './useToggleFavoriteTopic';
import {
  useAddTopicUser,
  useRemoveTopicUser,
  useRequestAccess,
} from './useTopicUserMutations';
import TopicUsersPanel, { TOPIC_JOIN_TIP } from './users/TopicUsersPanel';
import type { UserTopicAccessSource } from './utils/getTopicUsers';
import getTopicUsers, {
  getTopicAccessSource,
  TopicAccessSource,
} from './utils/getTopicUsers';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import type { GetTopicUsers_TopicFragment } from '../graphql/generated/graphql';

const TOPIC_FRAGMENT = gql(`
  fragment TopicViewHeader_topic on Topic {
    id
    name
    privacy
    memberEditable
    description
    templateCount
    postCounts {
      type
      count
    }
    ancestors {
      ...TopicBreadcrumbs_topics
    }
    banner {
      original
    }
    children {
      ...ReorderTopicsModal_topics
    }
    requestors {
      id
    }
    descendants(first: 0) {
      totalCount
    }
    ...ReorderTopicsModal_parentTopic
    ...topicPath_topic
    ...EditTopicModal_topic
    ...useAddTopicUser_topic
  }
`);

const getLeaveDisabledTooltip = (
  userAccessSource: UserTopicAccessSource,
): string | undefined => {
  let role: 'owner' | 'member' | undefined;
  let source: TopicAccessSource | undefined;

  (['member', 'owner'] as const).forEach((r) => {
    [TopicAccessSource.Inherited, TopicAccessSource.Group].forEach((s) => {
      if (userAccessSource[r][s]) {
        role = r;
        source = s;
      }
    });
  });

  const roleText = role === 'owner' ? 'an owner' : 'a member';
  if (source === TopicAccessSource.Group) {
    return `You are in a group that is ${roleText} of this topic`;
  }

  if (source === TopicAccessSource.Inherited) {
    return `You are ${roleText} of this topic through the parent topic`;
  }

  return undefined;
};

export const useTopicViewHeaderMenuItems = ({
  canJoin,
  canEdit,
  canDelete,
  hasJoined,
  hasDirectlyJoined,
  canAddSubtopic,
  onJoin,
  onLeave,
  topic,
  topicForPermissionCheck,
  onEditDetailsClick,
}: {
  canJoin: boolean;
  canEdit: boolean;
  canDelete: boolean;
  hasJoined: boolean;
  hasDirectlyJoined: boolean;
  canAddSubtopic: boolean;
  onJoin: () => unknown;
  onLeave: () => unknown;
  topic: TopicViewHeaderProps['topic'];
  topicForPermissionCheck: GetTopicUsers_TopicFragment | null;
  onEditDetailsClick: () => void;
}) => {
  const { isFavorited, toggleFavoriteTopic } = useToggleFavoriteTopic(topic);
  const { isPermittedToCreatePublicSubtopic } = usePublicContentPermissions();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.session.user);
  const deleteTopic = useDeleteTopic(topic.id);

  const requestAccess = useRequestAccess();

  const handleModifyClick = () => {
    dispatch(UIActions.openModal(EditTopicModal, { topic }));
  };

  const handleShowUsers = () =>
    dispatch(UIActions.openRightPanel(TopicUsersPanel));
  const onShowActivity = () =>
    dispatch(UIActions.openRightPanel(TopicActivitiesPanel));

  const handlePublicLinkCopy = async () => {
    await navigator.clipboard.writeText(fullOrgUrl(publicTopicPath(topic)));
    dispatch(
      UIActions.pushToast({
        level: 'success',
        message: 'Public topic link copied',
      }),
    );
  };

  const handleCreateSubtopic = () =>
    dispatch(UIActions.openModal(NewTopicModal, { parentId: topic.id }));

  const { memberEditable } = topic;

  const canRequest = !hasJoined && !canJoin;
  const hasRequested = canRequest && isRequestor(topic, user);

  const accessSource = topicForPermissionCheck
    ? getTopicAccessSource(topicForPermissionCheck, user.id)
    : null;

  const canLeave =
    hasJoined &&
    accessSource &&
    (accessSource.owner[TopicAccessSource.Direct] ||
      accessSource.member[TopicAccessSource.Direct]);

  const leaveDisabledTooltip =
    hasJoined &&
    !canLeave &&
    accessSource &&
    getLeaveDisabledTooltip(accessSource);

  const descendantsCount = topic.descendants?.totalCount ?? 0;

  const publishedPostsCount =
    topic.postCounts.find((count) => count.type === TopicPostsType.Published)
      ?.count ?? 0;

  const { owners, members } = topicForPermissionCheck
    ? getTopicUsers(topicForPermissionCheck)
    : { owners: [], members: [] };

  const whoCanEdit = `Only topic ${
    memberEditable === MEMBER_EDITABLE_ALL ? 'members' : 'owners'
  }`;
  const editDisabledTip = !canEdit && (
    <>
      {whoCanEdit} <br /> can edit this topic
    </>
  );
  const deleteDisabledTip = !canDelete && (
    <>
      {whoCanEdit} <br /> can delete this topic
    </>
  );

  const REQUESTED_MESSAGE =
    'Membership requested. You will receive an email when access is granted.';

  const isSubtopicPermitted =
    isPermittedToCreatePublicSubtopic || topic.privacy !== TopicPrivacy.Public;

  return [
    canJoin && (
      <MenuItem key="join" icon={addIcon} onClick={onJoin}>
        Join topic
      </MenuItem>
    ),
    canRequest && (
      <MenuItem
        key="request"
        icon={addIcon}
        disabled={hasRequested}
        onClick={async () => {
          await requestAccess(topic.id);
          dispatch(
            UIActions.pushToast({
              level: 'success',
              message: REQUESTED_MESSAGE,
            }),
          );
        }}
        tooltip={hasRequested ? REQUESTED_MESSAGE : undefined}
      >
        Request to join
      </MenuItem>
    ),
    topic.privacy === 'PUBLIC' && (
      <MenuItem key="copyLink" icon={copyIcon} onClick={handlePublicLinkCopy}>
        Copy public link
      </MenuItem>
    ),
    <TutorialBeacon key="edit" id={BEACON_TOPIC_EDIT} placement="left">
      {(beaconProps) => (
        <>
          <MenuItem
            {...beaconProps}
            icon={editIcon}
            onClick={onEditDetailsClick}
            tooltip={editDisabledTip}
            disabled={!canEdit}
          >
            Edit appearance
          </MenuItem>
          <MenuItem
            {...beaconProps}
            icon={settingsIcon}
            onClick={handleModifyClick}
            tooltip={editDisabledTip}
            disabled={!canEdit}
          >
            Modify settings
          </MenuItem>
        </>
      )}
    </TutorialBeacon>,
    canAddSubtopic && (
      <MenuItem
        key="subtopic"
        disabled={!isSubtopicPermitted}
        tooltip={
          isSubtopicPermitted
            ? undefined
            : 'Only admins can create subtopics of public topic'
        }
        icon={topicIcon}
        onClick={handleCreateSubtopic}
      >
        Create subtopic
      </MenuItem>
    ),
    canEdit && topic.children && topic.children.length > 1 && (
      <MenuItem
        key="reorder"
        icon={reorderIcon}
        onClick={() => {
          dispatch(
            UIActions.openModal(ReorderTopicsModal, {
              topics: topic.children,
              parentTopic: topic,
            }),
          );
        }}
      >
        Reorder subtopics
      </MenuItem>
    ),
    <MenuItem
      key="favorite"
      icon={favoriteIcon}
      onClick={() => toggleFavoriteTopic()}
    >
      {isFavorited ? 'Unfavorite topic' : 'Favorite topic'}
    </MenuItem>,
    hasJoined && (
      <MenuItem
        key="leave"
        disabled={!!leaveDisabledTooltip}
        icon={memberRemoveIcon}
        onClick={onLeave}
        tooltip={leaveDisabledTooltip}
      >
        Leave topic
      </MenuItem>
    ),
    <MenuSeparator key="actionsSeparator" />,
    <MenuItem key="activity" icon={clockIcon} onClick={onShowActivity}>
      Activity
    </MenuItem>,
    <MenuItem
      key="members"
      icon={memberIcon}
      onClick={handleShowUsers}
      disabled={members.length === 0 && owners.length === 0}
    >
      Members
    </MenuItem>,
    !IS_CAPACITOR && (
      <MenuItem
        key="contentMap"
        icon={subtopicsIcon}
        to={topicPath(topic, 'subtopics')}
        disabled={descendantsCount === 0 && publishedPostsCount === 0}
      >
        Content Map
      </MenuItem>
    ),
    <MenuSeparator key="separator" />,
    !hasDirectlyJoined && !canJoin && user.type === UserType.Admin && (
      <MenuItem
        key="joinAsAdmin"
        icon={addIcon}
        onClick={onJoin}
        right={AdminBadge}
      >
        Join topic
      </MenuItem>
    ),
    <MenuItem
      key="delete"
      onClick={() => {
        deleteTopic();
      }}
      danger
      icon={trashIcon}
      disabled={!canDelete}
      tooltip={deleteDisabledTip}
    >
      Delete topic
    </MenuItem>,
  ];
};

interface TopicViewHeaderProps {
  topic: DocumentType<typeof TOPIC_FRAGMENT>;
  editing: boolean;
  setIsEditing: (isEditing: boolean) => void;
  editTopicState: EditTopicState & { touched: boolean };
  updateEditTopicState: (payload: Partial<EditTopicState | null>) => void;
  resetEditTopicState: (t: EditTopicPayload) => void;
}

const TopicViewHeader = ({
  topic,
  editing,
  setIsEditing,
  editTopicState,
  updateEditTopicState,
  resetEditTopicState,
}: TopicViewHeaderProps) => {
  const topicMap = useAllTopicMap();
  const usersMap = useAllUsersMap();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.session.user);
  const isDescriptionCollapsed = useAppSelector(
    (state) => !!state.ui.descriptionCollapsedTopics[topic.id],
  );
  const bannerEditor = useRef<BannerEditorRef>(null);

  const { ancestors, banner } = topic;
  const currentBannerUrl = banner?.original;

  const [updateTopic, { loading: isSaving }] = useMutation(
    UPDATE_TOPIC_MUTATION,
    {
      variables: { id: topic.id },
    },
  );

  const saveTopic = async () => {
    const bannerFile = await bannerEditor.current?.getBannerFile();
    const nextBanner =
      editTopicState.banner?.image !== currentBannerUrl
        ? bannerFile
        : undefined;

    const { data } = await updateTopic({
      variables: {
        id: topic.id,
        name: editTopicState.name,
        description: JSON.stringify(editTopicState.description),
        banner: nextBanner,
        unsplashId: editTopicState.banner?.unsplashId,
        defaultSort: editTopicState.defaultSort,
        defaultShow: editTopicState.defaultShow,
      },
    });

    if (data?.updateTopic) {
      // we need to use flush sync here because otherwise the state update will be batched
      // with the redirect happening via setIsEditing(false), but we need to make sure that
      // the state is resetted before the navigation happens so that the confirm modal from
      // useBeforeRouterLeaveConfirmWithModal will not show up
      flushSync(() => {
        resetEditTopicState(data.updateTopic);
      });
    }
  };

  useBeforeRouterLeaveConfirmWithModal({
    skip: !editing || !editTopicState.touched,
    onConfirm: saveTopic,
    title: 'Save changes?',
    message: "Please save your changes if you'd like to keep them.",
    confirmButtonText: 'Save',
    cancelButtonText: 'Discard',
  });

  const handleSave = async () => {
    await saveTopic();
    setIsEditing(false);
  };

  const handleCancel = () => setIsEditing(false);

  const toggleDescription = () => {
    dispatch(UIActions.toggleTopicDescription(topic.id));
  };

  const topicForPermissionCheck = topicMap[topic.id] ?? null;

  const removeTopicUser = useRemoveTopicUser();
  const addTopicUser = useAddTopicUser();
  const handleJoin = () => addTopicUser(topic, user);
  const handleLeave = () =>
    topicForPermissionCheck && removeTopicUser(topicForPermissionCheck, user);

  const hasJoined =
    !!topicForPermissionCheck && isUserInTopic(topicForPermissionCheck, user);

  const hasDirectlyJoined =
    !!topicForPermissionCheck &&
    isUserDirectlyInTopic(topicForPermissionCheck, user);

  const canAddOrRemoveMember =
    !!topicForPermissionCheck &&
    topicPolicyCheck(
      TopicPolicyAction.AddRemoveMember,
      topicForPermissionCheck,
      user,
    );
  const canEdit =
    !!topicForPermissionCheck &&
    topicPolicyCheck(
      TopicPolicyAction.EditTopic,
      topicForPermissionCheck,
      user,
    );
  const canDelete =
    !!topicForPermissionCheck &&
    topicPolicyCheck(
      TopicPolicyAction.DeleteTopic,
      topicForPermissionCheck,
      user,
    );
  const canAddSubtopic =
    !!topicForPermissionCheck &&
    topicPolicyCheck(
      TopicPolicyAction.AddSubtopic,
      topicForPermissionCheck,
      user,
    );

  const canJoin = !hasJoined && canAddOrRemoveMember;

  const { owners, members } = topicForPermissionCheck
    ? getTopicUsers(topicForPermissionCheck)
    : { owners: [], members: [] };

  const topicUsers = populateObjects(usersMap, [...owners, ...members]);

  const menuItems = useTopicViewHeaderMenuItems({
    hasJoined,
    hasDirectlyJoined,
    canJoin,
    canEdit,
    canDelete,
    canAddSubtopic,
    onJoin: handleJoin,
    onLeave: handleLeave,
    topic,
    topicForPermissionCheck,
    onEditDetailsClick: () => setIsEditing(true),
  });

  const overflowMenu = (
    <div className={styles.overflownControls}>
      <OverflowMenu
        beaconId={BEACON_TOPIC_OVERFLOW_MENU}
        beaconPlacement="right"
      >
        {menuItems}
      </OverflowMenu>
    </div>
  );

  const joinButton = canJoin && (
    <Tooltip placement="left" tooltip={TOPIC_JOIN_TIP} trigger="hover">
      <Button variant="primary" onClick={handleJoin}>
        {isSize(Sizes.SM) ? 'Join' : 'Join topic'}
      </Button>
    </Tooltip>
  );

  const isShowingBanner = currentBannerUrl != null || editing;

  const bannerEditorState = (() => {
    if (isSaving) return 'saving';
    return editing ? 'editing' : 'viewing';
  })();

  return (
    <div className={styles.topicHeader}>
      {isShowingBanner && (
        <BannerEditor
          ref={bannerEditor}
          currentBannerUrl={currentBannerUrl}
          state={bannerEditorState}
          onBannerChange={(nextBanner) =>
            updateEditTopicState({ banner: nextBanner })
          }
          banner={editTopicState.banner}
        />
      )}

      <div
        className={classNames(styles.content, {
          [styles.hasBanner]: isShowingBanner,
          [styles.isEditing]: editing,
        })}
      >
        {ancestors && ancestors.length > 0 && (
          <div className={styles.ancestorsContainer}>
            <TopicBreadcrumbs topics={ancestors} link={!editing} />
          </div>
        )}
        <h1 className={styles.title}>
          {editing ? (
            <InPlaceEditable
              defaultValue={topic.name}
              onChange={(nextName) => updateEditTopicState({ name: nextName })}
              placeholder="Enter a Topic Name"
              autoFocus
            />
          ) : (
            <TopicNameWithIcon
              topic={topic}
              iconSize="large"
              showDefaultIcon
              tooltip
            />
          )}
        </h1>
        <div className={styles.controls}>
          {editing ? (
            <InPlaceEditControls
              saving={isSaving}
              savingDisabled={editTopicState.name === ''}
              onCancelClick={handleCancel}
              onSaveClick={handleSave}
            />
          ) : (
            <>
              <TopicActionButtons topicUsers={topicUsers} />
              {overflowMenu}
              {joinButton}
            </>
          )}
        </div>
        <div className={styles.description}>
          {editing ? (
            <RichTextDescriptionEditor
              readOnly={false}
              noTitleLine
              namespace="topicUserDescription"
              defaultValue={topic.description}
              onTextChange={(nextDescription) =>
                updateEditTopicState({ description: nextDescription })
              }
              contentPlaceholder="Write a description right here..."
            />
          ) : (
            <TopicOrUserCollapsibleContent
              collapsed={isDescriptionCollapsed}
              onCollapsedChange={toggleDescription}
              content={topic.description}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default TopicViewHeader;
